// src/components/EditorProducto.js
//
// Dialog modal con tabs para crear/editar un producto.
// Tabs:
//   1. General: código, nombre, descripción, categoría padre/hija, marca, tipo.
//   2. Precios e IVA: precio venta, costo, IVA, mayoreo.
//   3. Inventario: controla inventario, stock mín/máx, unidad de medida.
//   4. Foto: preview + subir/reemplazar/quitar (una foto por producto).
//
// Switches en el header: Activo, Visible en POS.
//
// El editor recibe:
//  - modo: 'crear' | 'editar'
//  - producto: al editar, el producto completo (para prefill).
//  - empresaId
//  - categorias: lista completa del catálogo maestro (padre + hijas mezcladas)
//  - onClose(): cierra
//  - onSaved(): cierra + refresca la lista arriba

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Tabs, Tab, Grid,
  TextField, MenuItem, Button, Alert, Stack, Chip, Switch, FormControlLabel,
  InputAdornment, Typography, IconButton, Avatar, CircularProgress, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import SaveIcon from '@mui/icons-material/Save';
import { productos as apiProductos, parametricas, movimientos as apiMov } from '../api/posApi';
import { Table, TableHead, TableRow, TableCell, TableBody, TableContainer } from '@mui/material';

const TabPanel = ({ children, value, index }) => (
  <Box hidden={value !== index} sx={{ pt: 3 }}>{value === index && children}</Box>
);

const emptyForm = {
  codigo: '', codigoBarras: '', codigoAlterno: '',
  nombre: '', descripcion: '', descripcionCorta: '',
  padreId: null, catalogoId: null, marcaId: null,
  unidadMedidaId: null, tarifaIVAId: null,
  tipoProducto: 'PRODUCTO', esBienOServicio: 'BIEN',
  precioVenta: '', costo: '', precioMayoreo: '', precioMinimo: '', cantidadMayoreo: '',
  controlaInventario: false, stockMinimo: '', stockMaximo: '',
  permiteDescuento: true, descuentoMaximo: '',
  visibleEnPOS: true, activo: true,
  imagenUrl: null,
};

const EditorProducto = ({ modo, producto, empresaId, categorias, onClose, onSaved, onError }) => {
  const editando = modo === 'editar';
  const [tab, setTab]         = useState(0);
  const [form, setForm]       = useState(emptyForm);
  const [guardando, setGuardando] = useState(false);
  const [unidades, setUnidades]   = useState([]);
  const [tarifas, setTarifas]     = useState([]);
  const [subiendoFoto, setSubiendoFoto] = useState(false);

  // Cargar paramétricas
  useEffect(() => {
    (async () => {
      try {
        const [u, t] = await Promise.all([parametricas.unidadesMedida(), parametricas.tarifasIva()]);
        setUnidades(Array.isArray(u) ? u : (u.data || []));
        setTarifas(Array.isArray(t) ? t : (t.data || []));
      } catch { /* silent */ }
    })();
  }, []);

  // Prefill al editar
  useEffect(() => {
    if (!editando) { setForm(emptyForm); return; }
    if (!producto) return;
    const catCatId = producto.categoriaCatalogoId ?? producto.CategoriaCatalogoId ?? null;
    // Resolver si el catalogoId elegido es padre o hija
    const cat = categorias.find(c => c.categoriaCatalogoId === catCatId);
    const padreId = cat?.categoriaPadreId || (cat ? cat.categoriaCatalogoId : null);
    const catalogoId = cat?.categoriaPadreId ? cat.categoriaCatalogoId : null;
    setForm({
      codigo:            producto.codigo ?? producto.Codigo ?? '',
      codigoBarras:      producto.codigoBarras ?? producto.CodigoBarras ?? '',
      codigoAlterno:     producto.codigoAlterno ?? producto.CodigoAlterno ?? '',
      nombre:            producto.nombre ?? producto.Nombre ?? '',
      descripcion:       producto.descripcion ?? producto.Descripcion ?? '',
      descripcionCorta:  producto.descripcionCorta ?? producto.DescripcionCorta ?? '',
      padreId, catalogoId,
      marcaId:           producto.marcaId ?? producto.MarcaId ?? null,
      unidadMedidaId:    producto.unidadMedidaId ?? producto.UnidadMedidaId ?? null,
      tarifaIVAId:       producto.tarifaIVAId ?? producto.TarifaIVAId ?? null,
      tipoProducto:      producto.tipoProducto ?? producto.TipoProducto ?? 'PRODUCTO',
      esBienOServicio:   producto.esBienOServicio ?? producto.EsBienOServicio ?? 'BIEN',
      precioVenta:       producto.precioVenta ?? producto.PrecioVenta ?? '',
      costo:             producto.costo ?? producto.Costo ?? '',
      precioMayoreo:     producto.precioMayoreo ?? producto.PrecioMayoreo ?? '',
      precioMinimo:      producto.precioMinimo ?? producto.PrecioMinimo ?? '',
      cantidadMayoreo:   producto.cantidadMayoreo ?? producto.CantidadMayoreo ?? '',
      controlaInventario: producto.controlaInventario ?? producto.ControlaInventario ?? false,
      stockMinimo:       producto.stockMinimo ?? producto.StockMinimo ?? '',
      stockMaximo:       producto.stockMaximo ?? producto.StockMaximo ?? '',
      permiteDescuento:  producto.permiteDescuento ?? producto.PermiteDescuento ?? true,
      descuentoMaximo:   producto.descuentoMaximo ?? producto.DescuentoMaximo ?? '',
      visibleEnPOS:      producto.visibleEnPOS ?? producto.VisibleEnPOS ?? true,
      activo:            producto.activo ?? producto.Activo ?? true,
      imagenUrl:         producto.imagenUrl ?? producto.ImagenUrl ?? null,
    });
  }, [editando, producto, categorias]);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((s) => ({ ...s, [k]: v }));
  };

  const padres = categorias.filter(c => !c.categoriaPadreId);
  const hijasDelPadre = form.padreId
    ? categorias.filter(c => c.categoriaPadreId === form.padreId)
    : [];

  const submit = async () => {
    if (!form.codigo.trim() || !form.nombre.trim()) return;
    setGuardando(true);
    try {
      // Categoría final: sub si hay, si no el padre.
      const catalogoIdFinal = form.catalogoId || form.padreId || null;
      const numOrNull = (v) => (v === '' || v === null || v === undefined) ? null : Number(v);
      const dto = {
        empresaId,
        codigo:          form.codigo.trim(),
        codigoBarras:    form.codigoBarras?.trim() || null,
        codigoAlterno:   form.codigoAlterno?.trim() || null,
        nombre:          form.nombre.trim(),
        descripcion:     form.descripcion?.trim() || null,
        descripcionCorta:form.descripcionCorta?.trim() || null,
        categoriaCatalogoId: catalogoIdFinal,
        marcaId:         form.marcaId || null,
        unidadMedidaId:  form.unidadMedidaId || null,
        tarifaIVAId:     form.tarifaIVAId || null,
        tipoProducto:    form.tipoProducto,
        esBienOServicio: form.esBienOServicio,
        precioVenta:     numOrNull(form.precioVenta) ?? 0,
        costo:           numOrNull(form.costo) ?? 0,
        precioMayoreo:   numOrNull(form.precioMayoreo),
        precioMinimo:    numOrNull(form.precioMinimo),
        cantidadMayoreo: numOrNull(form.cantidadMayoreo),
        controlaInventario: form.controlaInventario,
        stockMinimo:     numOrNull(form.stockMinimo) ?? 0,
        stockMaximo:     numOrNull(form.stockMaximo),
        permiteDescuento: form.permiteDescuento,
        descuentoMaximo: numOrNull(form.descuentoMaximo),
        visibleEnPOS:    form.visibleEnPOS,
        activo:          form.activo,
      };
      let productoIdFinal;
      if (editando) {
        await apiProductos.actualizar(producto.productoId, dto);
        productoIdFinal = producto.productoId;
      } else {
        const r = await apiProductos.crear(dto);
        productoIdFinal = r?.productoId ?? r?.ProductoId;
      }
      onSaved(productoIdFinal);
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo guardar.');
    } finally { setGuardando(false); }
  };

  const subirFoto = async (file) => {
    if (!editando) {
      onError('Guarda el producto primero para poder subir la foto.');
      return;
    }
    setSubiendoFoto(true);
    try {
      const r = await apiProductos.subirFoto(producto.productoId, file);
      setForm(s => ({ ...s, imagenUrl: r.imagenUrl || r.ImagenUrl }));
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo subir la foto.');
    } finally { setSubiendoFoto(false); }
  };

  const quitarFoto = async () => {
    if (!editando || !form.imagenUrl) return;
    if (!window.confirm('¿Eliminar la foto de este producto?')) return;
    setSubiendoFoto(true);
    try {
      await apiProductos.eliminarFoto(producto.productoId);
      setForm(s => ({ ...s, imagenUrl: null }));
    } catch (e) {
      onError(e.mensajeUsuario || 'No se pudo eliminar la foto.');
    } finally { setSubiendoFoto(false); }
  };

  const puedeGuardar = form.codigo.trim() && form.nombre.trim();

  return (
    <Dialog open onClose={() => !guardando && onClose()} fullWidth maxWidth="md"
            PaperProps={{ sx: { minHeight: 560 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} noWrap>
            {editando ? form.nombre || 'Editar producto' : 'Nuevo producto'}
          </Typography>
          {editando && (
            <Typography variant="caption" color="text.secondary">
              Código: {form.codigo}
            </Typography>
          )}
        </Box>
        <FormControlLabel
          control={<Switch checked={form.visibleEnPOS} onChange={upd('visibleEnPOS')} size="small" />}
          label="Visible en POS"
          sx={{ mr: 1 }}
        />
        <FormControlLabel
          control={<Switch checked={form.activo} onChange={upd('activo')} size="small" color="success" />}
          label="Activo"
        />
        <IconButton onClick={onClose} disabled={guardando}><CloseIcon /></IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tab label="General" />
        <Tab label="Precios e IVA" />
        <Tab label="Inventario" />
        <Tab label="Foto" disabled={!editando} />
        <Tab label="Movimientos" disabled={!editando} />
      </Tabs>
      <DialogContent>
        {/* GENERAL */}
        <TabPanel value={tab} index={0}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Código" required value={form.codigo} onChange={upd('codigo')}
                         helperText="Interno. Ej. P-001" />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField fullWidth label="Nombre" required value={form.nombre} onChange={upd('nombre')}
                         autoFocus />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código de barras" value={form.codigoBarras}
                         onChange={upd('codigoBarras')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Código alterno" value={form.codigoAlterno}
                         onChange={upd('codigoAlterno')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Categoría"
                         value={form.padreId || ''}
                         onChange={(e) => setForm(s => ({
                           ...s,
                           padreId: Number(e.target.value) || null,
                           catalogoId: null,
                         }))}
                         helperText="Del catálogo del tipo de negocio.">
                <MenuItem value="">(sin categoría)</MenuItem>
                {padres.map(p => (
                  <MenuItem key={p.categoriaCatalogoId} value={p.categoriaCatalogoId}>
                    {p.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Subcategoría"
                         value={form.catalogoId || ''} disabled={!form.padreId || hijasDelPadre.length === 0}
                         onChange={(e) => setForm(s => ({
                           ...s, catalogoId: Number(e.target.value) || null,
                         }))}
                         helperText={
                           !form.padreId ? 'Elige categoría primero' :
                           hijasDelPadre.length === 0 ? 'Sin subcategorías disponibles' : ''
                         }>
                <MenuItem value="">(sin subcategoría)</MenuItem>
                {hijasDelPadre.map(h => (
                  <MenuItem key={h.categoriaCatalogoId} value={h.categoriaCatalogoId}>
                    {h.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Tipo"
                         value={form.tipoProducto} onChange={upd('tipoProducto')}>
                <MenuItem value="PRODUCTO">Producto</MenuItem>
                <MenuItem value="SERVICIO">Servicio</MenuItem>
                <MenuItem value="COMBO">Combo</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Bien o Servicio"
                         value={form.esBienOServicio} onChange={upd('esBienOServicio')}>
                <MenuItem value="BIEN">Bien</MenuItem>
                <MenuItem value="SERVICIO">Servicio</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción corta"
                         value={form.descripcionCorta} onChange={upd('descripcionCorta')}
                         helperText="Máximo ~120 caracteres. Se muestra en listados." />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={3} label="Descripción completa"
                         value={form.descripcion} onChange={upd('descripcion')} />
            </Grid>
          </Grid>
        </TabPanel>

        {/* PRECIOS */}
        <TabPanel value={tab} index={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Precio de venta" type="number" required
                         value={form.precioVenta} onChange={upd('precioVenta')}
                         InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Costo" type="number"
                         value={form.costo} onChange={upd('costo')}
                         InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                         helperText="Costo unitario. Se actualiza al recibir mercancía." />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Precio mayoreo" type="number"
                         value={form.precioMayoreo} onChange={upd('precioMayoreo')}
                         InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Cantidad para mayoreo" type="number"
                         value={form.cantidadMayoreo} onChange={upd('cantidadMayoreo')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth label="Precio mínimo" type="number"
                         value={form.precioMinimo} onChange={upd('precioMinimo')}
                         InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                         helperText="Piso para descuentos manuales." />
            </Grid>
            <Grid item xs={12}><Divider><Chip label="IVA y descuentos" size="small" /></Divider></Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Tarifa de IVA"
                         value={form.tarifaIVAId || ''} onChange={upd('tarifaIVAId')}>
                <MenuItem value="">(sin especificar)</MenuItem>
                {tarifas.map(t => (
                  <MenuItem key={t.tarifaIVAId || t.TarifaIVAId} value={t.tarifaIVAId || t.TarifaIVAId}>
                    {t.nombre || t.Nombre} · {t.porcentaje ?? t.Porcentaje}%
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={
                <Switch checked={form.permiteDescuento} onChange={upd('permiteDescuento')} />
              } label="Permite descuento en el POS" />
              {form.permiteDescuento && (
                <TextField label="Descuento máximo %" type="number" size="small"
                           value={form.descuentoMaximo} onChange={upd('descuentoMaximo')}
                           sx={{ mt: 1, width: 180 }} />
              )}
            </Grid>
          </Grid>
        </TabPanel>

        {/* INVENTARIO */}
        <TabPanel value={tab} index={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Unidad de medida"
                         value={form.unidadMedidaId || ''} onChange={upd('unidadMedidaId')}>
                <MenuItem value="">(sin especificar)</MenuItem>
                {unidades.map(u => (
                  <MenuItem key={u.unidadMedidaId || u.UnidadMedidaId} value={u.unidadMedidaId || u.UnidadMedidaId}>
                    {u.nombre || u.Nombre} ({u.abreviatura || u.Abreviatura})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel control={
                <Switch checked={form.controlaInventario} onChange={upd('controlaInventario')} />
              } label="Controla inventario (descuenta stock al vender)" />
            </Grid>
            {form.controlaInventario && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Stock mínimo" type="number"
                             value={form.stockMinimo} onChange={upd('stockMinimo')}
                             helperText="Alerta cuando el stock cae por debajo." />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth label="Stock máximo" type="number"
                             value={form.stockMaximo} onChange={upd('stockMaximo')}
                             helperText="Referencia para el reabastecimiento." />
                </Grid>
              </>
            )}
          </Grid>
        </TabPanel>

        {/* FOTO */}
        <TabPanel value={tab} index={3}>
          {!editando ? (
            <Alert severity="info">Guarda el producto primero. Luego podrás subir su foto aquí.</Alert>
          ) : (
            <Stack alignItems="center" spacing={2} sx={{ py: 2 }}>
              <Avatar src={form.imagenUrl} variant="rounded"
                      sx={{ width: 220, height: 220, bgcolor: 'grey.100' }}>
                <Inventory2Icon sx={{ fontSize: 80, color: 'text.disabled' }} />
              </Avatar>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" component="label" startIcon={<PhotoCameraIcon />}
                        disabled={subiendoFoto}>
                  {form.imagenUrl ? 'Cambiar foto' : 'Subir foto'}
                  <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                         onChange={(e) => {
                           const f = e.target.files?.[0];
                           if (f) subirFoto(f);
                           e.target.value = '';
                         }} />
                </Button>
                {form.imagenUrl && (
                  <Button color="error" startIcon={<DeleteIcon />}
                          onClick={quitarFoto} disabled={subiendoFoto}>
                    Quitar foto
                  </Button>
                )}
              </Stack>
              {subiendoFoto && <CircularProgress size={24} />}
              <Typography variant="caption" color="text.secondary" textAlign="center">
                Formatos: JPG, PNG o WEBP. Máximo 5 MB.<br />
                Por ahora se guarda una sola foto. La galería queda para una versión futura.
              </Typography>
            </Stack>
          )}
        </TabPanel>

        {/* MOVIMIENTOS (solo lectura del kardex) */}
        <TabPanel value={tab} index={4}>
          <TabKardexProducto productoId={producto?.productoId} empresaId={empresaId}
                             onError={onError} />
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button variant="contained" onClick={submit} startIcon={<SaveIcon />}
                disabled={!puedeGuardar || guardando}>
          {guardando ? 'Guardando…' : (editando ? 'Guardar cambios' : 'Crear producto')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditorProducto;

// ── Subcomponente: kardex del producto (solo lectura) ─────────
function TabKardexProducto({ productoId, empresaId, onError }) {
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  useEffect(() => {
    if (!productoId || !empresaId) return;
    (async () => {
      setCargando(true);
      try {
        const r = await apiMov.kardex(productoId, empresaId, { limite: 50 });
        setItems(Array.isArray(r) ? r : []);
      } catch (e) { onError?.(e.mensajeUsuario || 'No se pudo cargar el kardex.'); }
      finally { setCargando(false); }
    })();
  }, [productoId, empresaId, onError]);

  const fmtNum = (n) => (Number(n) || 0).toLocaleString('es-CO', { maximumFractionDigits: 2 });
  const fmtFecha = (s) => { try { return new Date(s).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }); } catch { return s; } };

  if (cargando) return <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (items.length === 0) return <Alert severity="info">Sin movimientos registrados aún.</Alert>;
  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Últimos 50 movimientos. Para el kardex completo y registrar movimientos usa el botón ↕ en la lista de inventario.
      </Typography>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Motivo</TableCell>
              <TableCell align="right">Cantidad</TableCell>
              <TableCell align="right">Saldo</TableCell>
              <TableCell>Almacén</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(k => (
              <TableRow key={k.movimientoId}>
                <TableCell><Typography variant="caption">{fmtFecha(k.fecha)}</Typography></TableCell>
                <TableCell>
                  <Chip size="small" label={k.tipoMovimiento}
                        color={k.tipoMovimiento === 'ENTRADA' ? 'success'
                             : k.tipoMovimiento === 'SALIDA'  ? 'error'
                             : k.tipoMovimiento === 'TRANSFERENCIA' ? 'info' : 'default'}
                        variant="outlined" />
                </TableCell>
                <TableCell><Typography variant="caption">{k.motivo || '—'}</Typography></TableCell>
                <TableCell align="right">{fmtNum(Math.abs(k.cantidad))}</TableCell>
                <TableCell align="right"><b>{fmtNum(k.saldoDespues)}</b></TableCell>
                <TableCell><Typography variant="caption">{k.almacenNombre || '—'}</Typography></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
